// src/pages/PetDetailPage.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPetById } from '../services/petService';
import '../styles/PetDetailPage.css';

// Import icons
import { FaHeart, FaPaw, FaHome, FaChevronLeft, FaChevronRight, 
         FaVenusMars, FaWeight, FaBirthdayCake, FaMapMarkerAlt, 
         FaShieldAlt, FaStethoscope, FaUserFriends, FaWhatsapp, 
         FaEnvelope, FaPhoneAlt, FaGlobe, FaTimes, FaVideo } from 'react-icons/fa';

const PetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [pet, setPet] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchPet = async () => {
      try {
        setLoading(true);
        setError(null);
        setVideoError(false);
        
        if (!id) throw new Error('Pet ID is missing');
        
        const petId = parseInt(id, 10);
        const foundPet = await getPetById(petId);
        
        if (!foundPet) throw new Error('Pet not found');
        
        // Replace placeholder video URLs with real ones
        const updatedPet = {
          ...foundPet,
          images: foundPet.images.map((media: any) => {
            if (media.type === 'video') {
              // Handle both placeholder URLs and relative paths
              let videoUrl = media.url;
              
              if (videoUrl.includes('example.com')) {
                videoUrl = videoUrl.replace(
                  'https://example.com', 
                  'https://storage.googleapis.com/pet-videos-bucket'
                );
              }
              // Handle relative paths
              else if (videoUrl.startsWith('/')) {
                videoUrl = `${process.env.PUBLIC_URL}${videoUrl}`;
              }
              
              return {
                ...media,
                url: videoUrl,
                thumbnail: media.thumbnail || ''
              };
            }
            return media;
          })
        };
        
        setPet(updatedPet);
      } catch (err) {
        setError('Failed to load pet details');
      } finally {
        setLoading(false);
      }
    };

    fetchPet();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowReservationModal(false);
      }
    };

    if (showReservationModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [showReservationModal]);

  // Reset video when media changes
  useEffect(() => {
    if (videoRef.current && pet?.images[currentMediaIndex]?.type === 'video') {
      videoRef.current.load();
      videoRef.current.play().catch(e => {
        console.error('Autoplay failed:', e);
        setVideoError(true);
      });
    }
  }, [currentMediaIndex, pet]);

  const nextMedia = () => {
    if (pet) {
      setMediaLoaded(false);
      setVideoError(false);
      setCurrentMediaIndex(prev => (prev + 1) % pet.images.length);
    }
  };

  const prevMedia = () => {
    if (pet) {
      setMediaLoaded(false);
      setVideoError(false);
      setCurrentMediaIndex(prev => (prev - 1 + pet.images.length) % pet.images.length);
    }
  };

  const handleMediaLoad = () => {
    setMediaLoaded(true);
  };

  const handleReserveClick = () => {
    setShowReservationModal(true);
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/1234567890?text=Hi%20there,%20I'm%20interested%20in%20adopting%20${pet.name}`, '_blank');
  };

  const handleEmail = () => {
    window.location.href = `mailto:adoption@example.com?subject=Adoption%20Inquiry%20for%20${pet.name}&body=Hello,%0D%0A%0D%0AI'm%20interested%20in%20adopting%20${pet.name}.%0D%0A%0D%0APlease%20contact%20me%20with%20more%20information.`;
  };

  const handleCall = () => {
    window.location.href = 'tel:+1234567890';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading pet details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <div className="error-icon">!</div>
        <p>{error}</p>
        <Link to="/pets" className="retry-btn">Back to Adoption</Link>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="no-pet">
        <FaPaw className="no-pet-icon" />
        <p>Pet not found</p>
        <Link to="/pets" className="back-btn">Browse Available Pets</Link>
      </div>
    );
  }

  const currentMedia = pet.images[currentMediaIndex];

  return (
    <div className="pet-detail-page">
      <br/><br/><br/>
      {/* Reservation Modal */}
      {showReservationModal && (
        <div className="modal-overlay">
          <div className="reservation-modal" ref={modalRef}>
            <button 
              className="close-modal"
              onClick={() => setShowReservationModal(false)}
            >
              <FaTimes />
            </button>
            
            <div className="modal-header">
              <h2>Reserve {pet.name}</h2>
              <p>Contact our adoption agent to complete your reservation</p>
            </div>
            
            <div className="delivery-note">
              <FaGlobe className="globe-icon" />
              <span>We deliver worldwide to any country!</span>
            </div>
            
            <div className="contact-options">
              <div className="contact-option whatsapp" onClick={handleWhatsApp}>
                <div className="option-icon">
                  <FaWhatsapp />
                </div>
                <div className="option-details">
                  <h3>WhatsApp</h3>
                  <p>Chat with our agent</p>
                </div>
              </div>
              
              <div className="contact-option email" onClick={handleEmail}>
                <div className="option-icon">
                  <FaEnvelope />
                </div>
                <div className="option-details">
                  <h3>Email</h3>
                  <p>Send us a message</p>
                </div>
              </div>
              
              <div className="contact-option call" onClick={handleCall}>
                <div className="option-icon">
                  <FaPhoneAlt />
                </div>
                <div className="option-details">
                  <h3>Call</h3>
                  <p>Speak directly with us</p>
                </div>
              </div>
            </div>
            
            <div className="contact-info">
              <p>Or reach us directly at:</p>
              <p><strong>Phone:</strong> +1 (234) 567-890</p>
              <p><strong>Email:</strong> adoption@example.com</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="breadcrumb">
        <Link to="/"><FaHome className="breadcrumb-icon" /> Home</Link> 
        <span> &gt; </span>
        <Link to="/pets">Adoption</Link> 
        <span> &gt; </span>
        <span>{pet.name}</span>
      </div>
      
      <div className="pet-detail-container">
        <div className="pet-media">
          <div className="main-media-container">
            {currentMedia.type === 'image' ? (
              <img 
                src={currentMedia.url} 
                alt={pet.name} 
                className={`main-media ${mediaLoaded ? 'loaded' : ''}`}
                onLoad={handleMediaLoad}
              />
            ) : (
              <>
                {!videoError ? (
                  <video
                    ref={videoRef}
                    src={currentMedia.url}
                    className={`main-media ${mediaLoaded ? 'loaded' : ''}`}
                    controls
                    autoPlay
                    muted
                    playsInline
                    poster={currentMedia.thumbnail || ''}
                    onLoadedData={handleMediaLoad}
                    onError={() => setVideoError(true)}
                    key={`video-${currentMediaIndex}`}
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="video-fallback">
                    <p>Video failed to load</p>
                    <img 
                      src={currentMedia.thumbnail || ''} 
                      alt="Video thumbnail" 
                      className="fallback-thumbnail"
                    />
                  </div>
                )}
              </>
            )}
            
            {!mediaLoaded && <div className="media-skeleton"></div>}
            
            {pet.images.length > 1 && (
              <>
                <button className="media-nav prev" onClick={prevMedia}>
                  <FaChevronLeft />
                </button>
                <button className="media-nav next" onClick={nextMedia}>
                  <FaChevronRight />
                </button>
              </>
            )}
            
            <div className="media-counter">
              {currentMediaIndex + 1} / {pet.images.length}
              {currentMedia.type === 'video' && <FaVideo className="video-indicator" />}
            </div>
          </div>
          
          <div className="thumbnail-container">
            {pet.images.map((media: any, index: number) => (
              <div 
                key={index} 
                className={`thumbnail ${index === currentMediaIndex ? 'active' : ''}`}
                onClick={() => {
                  if (index !== currentMediaIndex) {
                    setMediaLoaded(false);
                    setVideoError(false);
                    setCurrentMediaIndex(index);
                  }
                }}
              >
                {media.type === 'image' ? (
                  <img 
                    src={media.url} 
                    alt={`${pet.name} thumbnail ${index + 1}`} 
                    loading="lazy"
                  />
                ) : (
                  <div className="video-thumbnail">
                    <img 
                      src={media.thumbnail || media.url} 
                      alt={`${pet.name} video thumbnail ${index + 1}`} 
                      loading="lazy"
                    />
                    <div className="video-play-icon">
                      <FaVideo />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        <div className="pet-info">
          <div className="pet-header">
            <div>
              <h1>{pet.name}</h1>
              <div className="adoption-actions">
                <button className="adopt-btn" onClick={handleReserveClick}>
                  Reserve {pet.name}
                </button>
              </div>
              <div className="worldwide-delivery">
                <FaGlobe className="delivery-icon" />
                <span>Worldwide Delivery Available</span>
              </div>
            </div>
            <div className="favorite-btn">
              <FaHeart />
            </div>
          </div>
          
          <div className="pet-meta-grid">
            <div className="meta-item">
              <FaPaw className="meta-icon" />
              <div>
                <span className="meta-label">Breed</span>
                <span className="meta-value">{pet.breed}</span>
              </div>
            </div>
            
            <div className="meta-item">
              <FaBirthdayCake className="meta-icon" />
              <div>
                <span className="meta-label">Age</span>
                <span className="meta-value">{pet.age}</span>
              </div>
            </div>
            
            <div className="meta-item">
              <FaWeight className="meta-icon" />
              <div>
                <span className="meta-label">Size</span>
                <span className="meta-value">{pet.size}</span>
              </div>
            </div>
            
            <div className="meta-item">
              <FaVenusMars className="meta-icon" />
              <div>
                <span className="meta-label">Gender</span>
                <span className="meta-value">{pet.gender}</span>
              </div>
            </div>
            
            <div className="meta-item">
              <FaMapMarkerAlt className="meta-icon" />
              <div>
                <span className="meta-label">Location</span>
                <span className="meta-value">{pet.location}</span>
              </div>
            </div>
            
            <div className="meta-item highlight">
              <div>
                <span className="meta-label">Adoption Fee</span>
                <span className="adoption-fee">${pet.adoptionFee}</span>
              </div>
            </div>
          </div>
          
          <div className="health-status">
            <div className={`status-tag ${pet.vaccinated ? 'positive' : 'negative'}`}>
              <FaShieldAlt className="status-icon" />
              {pet.vaccinated ? 'Vaccinated' : 'Not Vaccinated'}
            </div>
            <div className={`status-tag ${pet.neutered ? 'positive' : 'negative'}`}>
              <FaStethoscope className="status-icon" />
              {pet.neutered ? 'Neutered/Spayed' : 'Not Neutered/Spayed'}
            </div>
          </div>
          
          <div className="description-section">
            <h3>About {pet.name}</h3>
            <p>{pet.description}</p>
          </div>
          
          <div className="temperament-section">
            <h3>Personality & Temperament</h3>
            <div className="temperament-tags">
              {pet.temperament.map((trait: string, index: number) => (
                <span key={index} className="temperament-tag">
                  {trait}
                </span>
              ))}
            </div>
          </div>
          
          <div className="compatibility-section">
            <h3><FaUserFriends className="section-icon" /> Compatibility</h3>
            <div className="compatibility-tags">
              {pet.goodWith.map((item: string, index: number) => (
                <span key={index} className="compatibility-tag">
                  {item}
                </span>
              ))}
            </div>
          </div>
          
          <div className="story-section">
            <h3>{pet.name}'s Story</h3>
            <p>{pet.story}</p>
          </div>
          
          <div className="health-notes-section">
            <h3>Health Notes</h3>
            <p>{pet.healthNotes}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetDetailPage;